import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Scale, Lock, ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { Logo, Wordmark } from "@/components/Logo";

export default function CoursePreview() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/preview/courses/${slug}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [slug]);

  const cta = () => {
    if (!user) navigate("/register");
    else navigate(user.subscription_active || user.role === "admin" ? `/courses/${slug}` : "/subscribe");
  };

  if (data === null) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Course not found.</div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5"><Logo className="w-7 h-7" /><Wordmark className="font-serif text-xl" /></Link>
          <Link to="/preview" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> All previews</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-14">
        <span className="text-xs uppercase tracking-widest font-mono text-primary">Free Preview · {data.category}</span>
        <h1 className="font-serif text-5xl mt-3 mb-3 leading-tight">{data.title}</h1>
        <p className="text-muted-foreground mb-2">{data.summary}</p>
        <p className="text-xs font-mono text-muted-foreground mb-10">Lesson 1 of {data.total_lessons} · free to read</p>

        {data.lesson && (
          <article className="border border-border bg-card p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs uppercase tracking-widest font-mono text-primary">Lesson 1</span>
              <span className="text-xs font-mono text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{data.lesson.duration}</span>
            </div>
            <h2 className="font-serif text-3xl mb-6">{data.lesson.title}</h2>
            <div className="text-[15px] leading-[1.9] text-foreground/90 whitespace-pre-line">{data.lesson.body}</div>
          </article>
        )}

        <div className="mt-8 border border-primary bg-primary/5 p-8 text-center">
          <Lock className="w-7 h-7 text-primary mx-auto mb-4" />
          <h3 className="font-serif text-2xl mb-2">{data.total_lessons - 1} more lessons in this course</h3>
          <p className="text-sm text-muted-foreground mb-6">Unlock every lesson, flashcards, the dictionary, case-law search and the AI assistant for $4.44/month (or $44.44/year — one month free).</p>
          <button onClick={cta} data-testid="preview-cta" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-sm text-sm hover:-translate-y-[1px] transition-transform">
            {user ? "Continue this course" : "Create your account"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
